import { useState } from 'react'
import { Banner, Button } from '@sequencehq/core-components'
import { LinkAliasToCustomerModal } from '../components/modals/LinkAliasToCustomer/LinkAliasToCustomer.modal'
import { CreateCustomerForm } from 'Customers/components/drawers/CreateCustomer/CreateCustomerForm'

type LinkMode = 'existing' | 'new' | null

/**
 * Shared UI for resolving an event whose alias isn't mapped to a customer.
 * Returns render fragments for the three drawer slots (a warning banner, the
 * two link buttons, and the backing modals) so a caller can place each where
 * its layout needs it. Linking triggers a customer-cache invalidation, which
 * refetches the caller's mounted `getCustomerByAlias` query automatically — no
 * manual refetch is needed.
 */
export const useUnmappedAliasResolution = ({
  alias,
  testIdPrefix
}: {
  alias: string
  testIdPrefix: string
}) => {
  const [linkMode, setLinkMode] = useState<LinkMode>(null)

  const banner = (
    <Banner
      variant="warning"
      data-testid={`${testIdPrefix}.unmappedAlias.banner`}
    >
      This event's alias isn't mapped to a customer. Link it to a customer so
      the event is attributed correctly.
    </Banner>
  )

  const linkButtons = (
    <>
      <Button
        variant="primary"
        onClick={() => setLinkMode('existing')}
        data-testid={`${testIdPrefix}.linkToCustomer`}
        fullWidth
      >
        Link to an existing customer
      </Button>
      <Button
        variant="secondary"
        onClick={() => setLinkMode('new')}
        data-testid={`${testIdPrefix}.linkToNewCustomer`}
        fullWidth
      >
        Link to a new customer
      </Button>
    </>
  )

  const linkModals = (
    <>
      {linkMode === 'existing' && (
        <LinkAliasToCustomerModal
          alias={alias}
          onClose={() => setLinkMode(null)}
          onSuccess={() => setLinkMode(null)}
        />
      )}
      {linkMode === 'new' && (
        <CreateCustomerForm
          prefill={{ customerAliases: [alias] }}
          onClose={() => setLinkMode(null)}
          onSuccess={() => setLinkMode(null)}
        />
      )}
    </>
  )

  return { banner, linkButtons, linkModals }
}
